import React, { useEffect, useState , useRef } from "react";
import Breadcrums from "../../common/breadcrumbs";
import { useTranslation } from "react-i18next";
import CustomError from "../../../../utils/customError";
import { useFormik } from "formik";
import {
  globalLoader,
  handleServerValidations,
} from "../../../../utils/commonfunction";
import Swal from "sweetalert2";
import { TempleAdd } from "../../services/temple.services";
import { SWAL_SETTINGS } from "../../../../utils/Constants";
import { useNavigate } from "react-router-dom";
import * as Yup from "yup";
import { Loader } from "@googlemaps/js-api-loader";


const AddTemple = (props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [submitted, setSubmitted] = useState(false);
  const inputRef = useRef(null);


  const breadcrumbs = [
    { title: t("link_dashboard"), url: "/admin/dashboard" },
    { title: t("Temple"), url: "/admin/temples/list/1" },
    { title: t("Add"), url: "" },
  ];

  // ==================    Validation Rules ==========================

  const validationSchema = Yup.object().shape({
    templeName: Yup.string()
      .required("Temple name is required")
      .test("max-words", "Temple name must be maximum 30 words", (value) =>
        value ? value.trim().split(/\s+/).length <= 30 : true,
      ),

    templeLocation: Yup.string()
      .required("Temple location is required")
      .min(10, "Temple location must be at least 10 characters")
      .max(80, "Temple location must not exceed 50 characters"),

    templeDesc: Yup.string()
      .required("Temple description is required")
      .test(
        "max-words",
        "Temple description must be maximum 50 words",
        (value) => (value ? value.trim().split(/\s+/).length <= 50 : true),
      ),

    templeImage: Yup.mixed()
      .required("Temple image is required")
      .test("fileType", "Only JPG, JPEG, PNG files are allowed", (value) => {
        if (!value) return false;

        const fileExtension = value.name.split(".").pop().toLowerCase();
        return ["jpg", "jpeg", "png"].includes(fileExtension);
      })
  });

  const formik = useFormik({
    initialValues: {
      templeName: "",
      templeLocation: "",
      templeDesc: "",
      templeImage: "",
    },
    validateOnBlur: false,
    validationSchema: validationSchema,
    onSubmit: (values, { setSubmitting, resetForm }) => {
      globalLoader(true);
      setSubmitted(true);
      let formData = new FormData();
      formData.append("templeName", values.templeName);
      formData.append("templeLocation", values.templeLocation);
      formData.append("templeDesc", values.templeDesc);
      formData.append("templeImage", values.templeImage);

      TempleAdd(formData)
        .then((response) => {
          setSubmitting(false);
          if (response.success) {
            Swal.fire({
              icon: "success",
              text: response.message,
              ...SWAL_SETTINGS,
            });
            globalLoader(false);
            resetForm({ values: "" });
            navigate(`/admin/temples/list/1`);
          } else {
            Swal.fire({
              icon: "error",
              text: handleServerValidations(response),
              ...SWAL_SETTINGS,
            });
            globalLoader(false);
            setSubmitted(false);
          }
        })
        .catch((error) => {
          console.log("error ====> ", error);
        });
    },
  });


  useEffect(() => {
    const loader = new Loader({
      apiKey: process.env.REACT_APP_GOOGLE_MAP_API_KEY,
      version: "weekly",
      libraries: ["places"],
    });

    let autocomplete;
    loader.importLibrary("places").then(({ Autocomplete }) => {
      if (!inputRef.current) return;

      autocomplete = new Autocomplete(inputRef.current, {
        fields: ["formatted_address"],
        types: ["geocode"],
      });

      autocomplete.addListener("place_changed", () => {
        const place = autocomplete.getPlace();
        if (!place.formatted_address) return;
        formik.setFieldValue(
          "templeLocation",
          place.formatted_address
        );
      });
    });

    return () => {
      if (autocomplete) {
        window.google.maps.event.clearInstanceListeners(autocomplete);
      }
    };
  },[]);


  return (
    <>
      <Breadcrums data={breadcrumbs} />
      <form onSubmit={formik.handleSubmit}>
        <div className="row row-sm">
          <div className="col-lg-12 col-md-12 animation_fade">
            <div className="card custom-card">
              <div className="card-body">
                <div>
                  <h6 className="main-content-label mb-3">{t("Add Temple")}</h6>
                </div>
                <div className="row row-sm">
                  <div className="col-md-6 text-center form-group">
                    <label htmlFor="name" className="text-left d-flex">
                      {t("Temple Name")}:
                      <span className="requirestar">*</span>{" "}
                    </label>
                    <input
                      name="templeName"
                      id="templeName"
                      type="text"
                      onChange={(event) => {
                        console.log(event.target.value);
                        formik.handleChange(event);
                      }}
                      onBlur={formik.handleBlur}
                      value={formik.values.name}
                      className="form-control"
                      placeholder={t("Enter Temple Name")}
                    />
                    <span className="text-danger d-flex text-left">
                      <CustomError name="templeName" form={formik} />
                    </span>
                  </div>
                  <div className="col-md-6 text-center form-group">
                    <label htmlFor="name" className="text-left d-flex">
                      {t("Temple Location")}:
                      <span className="requirestar">*</span>{" "}
                    </label>
                    <input
                      ref={inputRef} 
                      name="templeLocation"
                      id="templeLocation"
                      type="text"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.templeLocation}
                      className="form-control"
                      placeholder={t("Enter Temple Location")}
                    />
                    <span className="text-danger d-flex text-left">
                      <CustomError name="templeLocation" form={formik} />
                    </span>
                  </div>
                  <div className="col-md-12 text-center form-group">
                    <label htmlFor="templeImage" className="text-left d-flex">
                      {t("Temple Image")}:<span className="requirestar">*</span>
                    </label>
                    <input
                      type="file"
                      name="templeImage"
                      id="templeImage"
                      className="form-control"
                      accept="image/png, image/jpeg, image/jpg"
                      onChange={(event) => {
                        formik.setFieldValue(
                          "templeImage",
                          event.currentTarget.files[0],
                        );
                      }}
                      onBlur={formik.handleBlur}
                    />
                    <span className="text-danger d-flex text-left">
                      <CustomError name="templeImage" form={formik} />
                    </span>
                  </div>

                  <div className="col-md-12 text-center form-group">
                    <label htmlFor="name" className="text-left d-flex">
                      {t("Temple Descripton")}:
                      <span className="requirestar">*</span>{" "}
                    </label>
                    <input
                      name="templeDesc"
                      id="templeDesc"
                      type="text"
                      onChange={(event) => {
                        formik.handleChange(event);
                      }}
                      onBlur={formik.handleBlur}
                      value={formik.values.name}
                      className="form-control"
                      placeholder={t("Enter Temple Description")}
                    />
                    <span className="text-danger d-flex text-left">
                      <CustomError name="templeDesc" form={formik} />
                    </span>
                  </div>

                  <div className="mt-3">
                    <button
                      className="btn btn-info mr-2"
                      type="submit"
                      disabled={submitted ? true : null}
                    >
                      <i className="ace-icon fa fa-check bigger-110 mx-1"></i>
                      {t("btn_submit")}
                    </button>
                    <button
                      className="btn ripple btn-secondary"
                      type="button"
                      disabled={submitted ? true : null}
                      onClick={() => navigate(-1)}
                    >
                      <i className="ace-icon fa fa-times bigger-110 mx-1"></i>
                      {t("btn_cancel")}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </form>
    </>
  );
};

export default AddTemple;
